import React, {useState, useEffect, useMemo, useRef} from 'react';
import {FaChevronDown, FaChevronRight, FaFileAlt, FaFolder, FaPlus} from 'react-icons/fa';
import {getSearchResultDetails} from './search-result-utils';
import {findSearchTextRanges, normalizeSearchText} from '../../services/song-search-text';
import {searchSnippet, buildSearchCorpus} from '../../services/song-search-model';

export const HighlightedRanges = ({text = '', ranges = []}) => {
  let cursor = 0;
  const parts = [];
  ranges.forEach(({start, end}, index) => {
    const from = Math.max(cursor, Math.min(text.length, start));
    const to = Math.max(from, Math.min(text.length, end));
    parts.push(text.slice(cursor, from));
    if (to > from) parts.push(<mark key={index}>{text.slice(from, to)}</mark>);
    cursor = to;
  });
  parts.push(text.slice(cursor));
  return <>{parts}</>;
};

export const SearchResultCardComponent = ({result, query, onOpen, onAdd, stale}) => {
  const [expanded, setExpanded] = useState(false);
  const variants = useMemo(() => result.variants || [result], [result]);
  const [selectedId, setSelectedId] = useState(result.matchedVariantId || variants[0].id);
  const choice = useRef(null);
  useEffect(() => {
    const selected = choice.current;
    const keep = selected?.group === result.id && selected.query === query && variants.some(variant => variant.id === selected.id);
    setSelectedId(keep ? selected.id : result.matchedVariantId || variants[0].id);
  }, [result.matchedVariantId, result.id, variants, query]);
  const selected = variants.find(variant => variant.id === selectedId)
    || variants.find(variant => variant.id === result.matchedVariantId) || variants[0];
  const details = getSearchResultDetails(selected);
  const terms = useMemo(() => normalizeSearchText(query).split(' ').filter(Boolean), [query]);
  const excerpt = useMemo(() => selected.id === result.sourceId ? result
    : selected.spans ? searchSnippet(selected, terms, buildSearchCorpus(variants))
      : {snippet: details.snippet, snippetRanges: findSearchTextRanges(details.snippet, terms),
        titleRanges: findSearchTextRanges(details.title, terms)}, [selected, result, terms, variants, details.snippet, details.title]);
  // The initial excerpt is the one ranked by the full corpus; switching versions
  // computes that version's own ranges rather than reusing another source's offsets.
  const snippet = selected.id === result.sourceId ? result.snippet : excerpt.snippet;
  const ranges = selected.id === result.sourceId ? result.snippetRanges : excerpt.snippetRanges;
  return <article className={`search-result-card${expanded ? ' search-result-expanded' : ''}`}>
    <button type="button" className="search-result-header" aria-expanded={expanded}
      aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} podgląd: ${details.title}`} onClick={() => setExpanded(!expanded)}>
      <span className="search-result-main">
        <span className="search-result-title"><HighlightedRanges text={details.title} ranges={excerpt.titleRanges} /></span>
        {snippet && <span className="search-result-snippet"><HighlightedRanges text={snippet} ranges={ranges} /></span>}
      </span>
      <span className="search-result-count">{variants.length} {variants.length === 1 ? 'lokalizacja' : 'lokalizacji'}
        {expanded ? <FaChevronDown /> : <FaChevronRight />}</span>
    </button>
    <span className="search-result-crumbs"><FaFolder aria-hidden="true" />
      {details.folders.map((folder, index) => <React.Fragment key={index}>
        {index > 0 && <FaChevronRight className="search-result-separator" aria-hidden="true" />}<span>{folder}</span>
      </React.Fragment>)}
    </span>
    <div className="search-result-foot"><FaFileAlt aria-hidden="true" /><span>{details.fileName}</span>
      <button type="button" className="search-result-open" onClick={() => onOpen(selected)}>Otwórz pieśń</button>
    </div>
    {expanded && <div className="search-result-details">
      {onAdd && <button type="button" onClick={() => onAdd(selected)} disabled={stale}><FaPlus aria-hidden="true" /> Dodaj do otwartego zestawu</button>}
      <div className="search-result-preview" aria-label={`Podgląd: ${details.title}`}>
        {selected.previewLines?.length ? selected.previewLines.map((line, index) => <div key={index}
          className={line.type === 'chord' ? 'search-preview-chords' : ''}>{line.text || '\u00a0'}</div>)
          : <pre>{selected.lyrics || selected.content}</pre>}
      </div>
      <h2>Wersje i lokalizacje ({variants.length})</h2>
      <div className="search-result-variants">{variants.map(variant => <button type="button" key={variant.id}
        aria-pressed={variant.id === selected.id} onClick={() => {choice.current = {group: result.id, query, id: variant.id}; setSelectedId(variant.id);}}>
        <FaFileAlt aria-hidden="true" /><span>{variant.fileName}</span><span>#{variant.songNumber || 1}</span>
      </button>)}</div>
    </div>}
  </article>;
};
